import React, { useState, useEffect } from 'react';

// Layout Components
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import MobileBottomNav from '../components/layout/MobileBottomNav';

// Modal Components
import MobileMenuModal from '../components/modals/MobileMenuModal';

// Page Components
import DashboardContent from '../pages/DashboardContent';
import CardListContent from '../pages/CardListContent';
import CharacterDetailContent from '../pages/CharacterDetailContent';
import NoteListContent from '../pages/NoteListContent';
import NoteDetailContent from '../pages/NoteDetailContent';
import MasterUserContent from '../pages/MasterUserContent';
import ProductManagementContent from '../pages/ProductManagementContent';
import PlaceholderContent from '../pages/PlaceholderContent';

// Config
import { MENU_ITEMS } from '../config/menuConfig';

// Konstanta
const MOBILE_MENU_LIMIT = 4;

/**
 * Layout utama untuk halaman admin
 * @param {function} onLogout - Callback untuk logout
 * @param {boolean} isDarkMode - Status dark mode
 * @param {function} toggleTheme - Callback untuk toggle theme
 */
const AdminLayout = ({ onLogout, isDarkMode, toggleTheme }) => {
    // State Management
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeMenu, setActiveMenu] = useState('dashboard');
    const [expandedMenus, setExpandedMenus] = useState(['master-data']);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [mobileModalOpen, setMobileModalOpen] = useState(false);
    const [mobileModalContent, setMobileModalContent] = useState({ title: '', items: [] });
    const [selectedCharacter, setSelectedCharacter] = useState(null);
    const [selectedStoryboard, setSelectedStoryboard] = useState(null);

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClick = () => setUserMenuOpen(false);
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    /**
     * Handle menu click - toggle submenu atau set active menu
     */
    const handleMenuClick = (item, isMobile = false) => {
        if (item.subItems) {
            if (isMobile) {
                setMobileModalContent({ title: item.label, items: item.subItems });
                setMobileModalOpen(true);
            } else {
                setExpandedMenus(prev =>
                    prev.includes(item.id)
                        ? prev.filter(id => id !== item.id)
                        : [...prev, item.id]
                );
            }
        } else {
            setActiveMenu(item.id);
            setSelectedCharacter(null);
            setSelectedStoryboard(null);
            if (isMobile) setMobileModalOpen(false);
        }
    };

    /**
     * Render konten berdasarkan menu aktif
     */
    const renderContent = () => {
        switch (activeMenu) {
            case 'dashboard':
                return <DashboardContent isDarkMode={isDarkMode} />;
            case 'card-list':
                if (selectedCharacter) {
                    return (
                        <CharacterDetailContent
                            character={selectedCharacter}
                            isDarkMode={isDarkMode}
                            onBack={() => setSelectedCharacter(null)}
                        />
                    );
                }
                return <CardListContent isDarkMode={isDarkMode} onSelectCharacter={setSelectedCharacter} />;
            case 'note-list':
                if (selectedStoryboard) {
                    return (
                        <NoteDetailContent
                            storyboard={selectedStoryboard}
                            isDarkMode={isDarkMode}
                            onBack={() => setSelectedStoryboard(null)}
                        />
                    );
                }
                return <NoteListContent isDarkMode={isDarkMode} onSelectStoryboard={setSelectedStoryboard} />;
            case 'master-user':
                return <MasterUserContent isDarkMode={isDarkMode} />;
            case 'product-management':
                return <ProductManagementContent isDarkMode={isDarkMode} />;
            case 'analytics':
            case 'messages':
            case 'reports':
            case 'help':
                return (
                    <PlaceholderContent
                        title={MENU_ITEMS.find(i => i.id === activeMenu)?.label || activeMenu}
                        isDarkMode={isDarkMode}
                    />
                );
            default:
                return <DashboardContent isDarkMode={isDarkMode} />;
        }
    };

    // Menu items untuk mobile navigation
    const primaryMobileItems = MENU_ITEMS.slice(0, MOBILE_MENU_LIMIT);
    const secondaryMobileItems = MENU_ITEMS.slice(MOBILE_MENU_LIMIT);

    /**
     * Handle klik tombol "Lainnya" di mobile
     */
    const handleMoreClick = () => {
        setMobileModalContent({ title: 'Menu Lainnya', items: secondaryMobileItems });
        setMobileModalOpen(true);
    };

    return (
        <div className={`
            flex h-screen w-full transition-colors duration-300 overflow-hidden 
            ${isDarkMode ? 'bg-slate-900 text-slate-200' : 'bg-slate-50 text-slate-800'}
        `}>
            {/* Desktop Sidebar */}
            <Sidebar
                isOpen={sidebarOpen}
                isDarkMode={isDarkMode}
                menuItems={MENU_ITEMS}
                activeMenu={activeMenu}
                expandedMenus={expandedMenus}
                onMenuClick={(item) => handleMenuClick(item, false)}
                onSubMenuClick={setActiveMenu}
            />

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-w-0 relative">
                {/* Header */}
                <Header
                    isDarkMode={isDarkMode}
                    toggleTheme={toggleTheme}
                    activeMenu={activeMenu}
                    userMenuOpen={userMenuOpen}
                    setUserMenuOpen={setUserMenuOpen}
                    onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                    onLogout={onLogout}
                />

                {/* Main Content */}
                <main className={`flex-1 overflow-auto p-4 md:p-6 ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50/50'}`}>
                    {renderContent()}
                </main>
            </div>

            {/* Mobile Bottom Navigation */}
            <MobileBottomNav
                isDarkMode={isDarkMode}
                primaryItems={primaryMobileItems}
                secondaryItems={secondaryMobileItems}
                activeMenu={activeMenu}
                onMenuClick={(item) => handleMenuClick(item, true)}
                onMoreClick={handleMoreClick}
            />

            {/* Mobile Menu Modal */}
            <MobileMenuModal
                isOpen={mobileModalOpen}
                onClose={() => setMobileModalOpen(false)}
                title={mobileModalContent.title}
                items={mobileModalContent.items}
                onSelect={(item) => handleMenuClick(item, true)}
                isDarkMode={isDarkMode}
                activeMenu={activeMenu}
            />
        </div>
    );
};

export default AdminLayout;
